import { BigNumber } from '@ethersproject/bignumber';
import cn from 'classnames';
import { InputField } from 'fields/base/InputField/InputField';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDexGasPrice } from '@/entities/dex';

import { IPairWallet } from 'api/apiPairs/models';
import { humanizeBn } from 'tools/math';
import { IDexPair } from 'types/pairs';
import { TableAddressChip } from 'ui/Table/TableAddressChip/TableAddressChip';

import { SourceWalletSummary } from './SourceWalletSummary';

import { getWalletAvailableWei } from '../../lib/tokens';
import { allocateEvenly, amountToWei, sumRecipientsWei, weiToAmount } from '../../model/allocation';
import { useTransferWizardStore } from '../../model/transferWizard.store';
import { validateAllocations } from '../../model/validators';

interface StepAllocateProps {
  pair: IDexPair;
  sourceWallet: IPairWallet;
  onValidityChange: (isValid: boolean) => void;
}

export const StepAllocate = ({ pair, sourceWallet, onValidityChange }: StepAllocateProps) => {
  const token = useTransferWizardStore(state => state.token);
  const recipients = useTransferWizardStore(state => state.recipients);
  const setRecipients = useTransferWizardStore(state => state.setRecipients);
  const totalAmount = useTransferWizardStore(state => state.totalAmount);
  const setTotalAmount = useTransferWizardStore(state => state.setTotalAmount);
  const errors = useTransferWizardStore(state => state.errors);
  const setErrors = useTransferWizardStore(state => state.setErrors);
  const [lastEditedWalletId, setLastEditedWalletId] = useState<number | null>(null);

  const gasPrice = useDexGasPrice({ network: pair.network });

  const rawAvailableWei = useMemo(() => {
    if (!token) return BigNumber.from(0);
    return getWalletAvailableWei({ wallet: sourceWallet, token });
  }, [token, sourceWallet]);

  // Calculate estimated gas fees
  const estimatedGasFees = useMemo(() => {
    const defaultValue = {
      gasFeeWei: BigNumber.from(0),
      gasFeeFormatted: '0',
      gasSymbol: gasPrice?.token.symbol ?? token?.symbol ?? '',
    };

    if (!token || !gasPrice || recipients.length === 0) {
      return defaultValue;
    }

    const isNativeToken = token.type === 'native' || token.id === pair.token_fee.id;
    // Native token transfer: 21000 gas units
    // ERC20 token transfer: 55000 gas units
    const gasUnitsPerTransfer = isNativeToken ? 21000 : 55000;
    const totalGasUnits = gasUnitsPerTransfer * recipients.length;

    const gasPriceWei = BigNumber.from(gasPrice.price);
    const gasFeeWei = gasPriceWei.mul(totalGasUnits);
    const gasFeeFormatted = humanizeBn(gasFeeWei, gasPrice.token.decimals);

    return { gasFeeWei, gasFeeFormatted, gasSymbol: gasPrice.token.symbol };
  }, [gasPrice, pair.token_fee.id, recipients.length, token]);

  // Check native currency balance for gas fees (for ERC20 transfers)
  const nativeBalanceWei = useMemo(() => {
    const nativeToken = sourceWallet.tokens?.find(
      walletToken => walletToken.token.id === pair.token_fee.id,
    );
    return nativeToken ? BigNumber.from(nativeToken.balance) : BigNumber.from(0);
  }, [sourceWallet, pair.token_fee.id]);

  // Check if there's enough native currency for gas
  const hasInsufficientGasBalance = useMemo(() => {
    if (!token || !gasPrice || recipients.length === 0) return false;

    const isNativeToken = token.type === 'native' || token.id === pair.token_fee.id;

    // For ERC20 tokens, check if native balance covers gas fees
    if (!isNativeToken) {
      return estimatedGasFees.gasFeeWei.gt(nativeBalanceWei);
    }

    // For native tokens, check if balance covers transfer + gas
    return false; // This is handled in availableWei calculation
  }, [
    token,
    gasPrice,
    recipients.length,
    pair.token_fee.id,
    estimatedGasFees.gasFeeWei,
    nativeBalanceWei,
  ]);

  // Available balance after deducting gas fees (only for native tokens)
  const availableWei = useMemo(() => {
    if (!token) return BigNumber.from(0);

    const isNativeToken = token.type === 'native' || token.id === pair.token_fee.id;

    // For native tokens, subtract gas fees from available balance
    if (isNativeToken && estimatedGasFees.gasFeeWei.gt(0)) {
      if (estimatedGasFees.gasFeeWei.gte(rawAvailableWei)) {
        return BigNumber.from(0);
      }

      return rawAvailableWei.sub(estimatedGasFees.gasFeeWei);
    }

    // For ERC20 tokens, return full token balance (gas is paid in native currency)
    return rawAvailableWei;
  }, [estimatedGasFees.gasFeeWei, pair.token_fee.id, rawAvailableWei, token]);

  useEffect(() => {
    if (!token) return;

    if (recipients.length === 0) {
      if (lastEditedWalletId !== null) {
        setLastEditedWalletId(null);
      }
      if (totalAmount !== '') {
        setTotalAmount('');
      }
      return;
    }

    const allEmpty = recipients.every(recipient => !recipient.amount);

    if (allEmpty) {
      if (totalAmount !== '') {
        setTotalAmount('');
      }
      return;
    }

    const sumWei = sumRecipientsWei(recipients, token.decimals);
    const sumHuman = weiToAmount(sumWei, token.decimals);

    if (sumHuman !== totalAmount) {
      setTotalAmount(sumHuman);
    }
  }, [recipients, token, totalAmount, setTotalAmount, lastEditedWalletId]);

  useEffect(() => {
    if (!token) return;

    const validation = validateAllocations({
      recipients,
      totalAmount,
      decimals: token.decimals,
      availableBalanceWei: availableWei,
    });

    if (validation.total === 'Total exceeds available balance' && lastEditedWalletId !== null) {
      validation.recipients = {
        ...validation.recipients,
        [lastEditedWalletId]:
          validation.recipients[lastEditedWalletId] ?? 'Exceeds available balance',
      };
    }

    // Add gas balance error for ERC20 tokens
    if (hasInsufficientGasBalance && !validation.total) {
      validation.total = `Insufficient ${estimatedGasFees.gasSymbol} balance for gas fees`;
    }

    setErrors(validation);

    const hasRecipientErrors = Object.values(validation.recipients).some(Boolean);
    const isValid = !validation.total && !hasRecipientErrors && !hasInsufficientGasBalance;
    onValidityChange(isValid && recipients.length > 0);
  }, [
    recipients,
    totalAmount,
    token,
    availableWei,
    setErrors,
    onValidityChange,
    lastEditedWalletId,
    hasInsufficientGasBalance,
    estimatedGasFees.gasSymbol,
  ]);

  const handleRecipientAmountChange = useCallback(
    (walletId: number, value: string) => {
      const currentRecipient = recipients.find(recipient => recipient.walletId === walletId);
      if (currentRecipient && currentRecipient.amount === value) {
        return;
      }

      setLastEditedWalletId(walletId);

      const nextRecipients = recipients.map(recipient =>
        recipient.walletId === walletId ? { ...recipient, amount: value } : recipient,
      );

      setRecipients(nextRecipients);

      if (token) {
        const sumWei = sumRecipientsWei(nextRecipients, token.decimals);
        setTotalAmount(weiToAmount(sumWei, token.decimals));
      }
    },
    [recipients, setRecipients, setTotalAmount, token],
  );

  const handleApplyEvenSplit = useCallback(
    (amount?: string) => {
      if (!token) return;
      if (recipients.length === 0) return;
      setLastEditedWalletId(null);

      const resolveTargetAmount = () => {
        if (amount !== undefined) {
          return amount.trim() !== '' ? amount : '';
        }

        if (totalAmount && totalAmount.trim() !== '') {
          return totalAmount;
        }

        const currentSumWei = sumRecipientsWei(recipients, token.decimals);
        if (!currentSumWei.isZero()) {
          return weiToAmount(currentSumWei, token.decimals);
        }

        if (!availableWei.isZero()) {
          return weiToAmount(availableWei, token.decimals);
        }

        return '';
      };

      const targetAmount = resolveTargetAmount();

      if (targetAmount === '') {
        const clearedRecipients = recipients.map(recipient =>
          recipient.amount === '' ? recipient : { ...recipient, amount: '' },
        );
        setRecipients(clearedRecipients);
        if (totalAmount !== '') {
          setTotalAmount('');
        }
        return;
      }

      const nextRecipients = allocateEvenly(targetAmount, recipients, token.decimals);
      setRecipients(nextRecipients);
      setTotalAmount(targetAmount);
    },
    [availableWei, recipients, setRecipients, setTotalAmount, token, totalAmount],
  );

  const handleTotalChange = useCallback(
    (value: string) => {
      if (value !== totalAmount) {
        setTotalAmount(value);
      }
      handleApplyEvenSplit(value);
    },
    [handleApplyEvenSplit, setTotalAmount, totalAmount],
  );

  const handleMax = useCallback(() => {
    if (!token) return;
    const human = weiToAmount(availableWei, token.decimals);
    handleApplyEvenSplit(human);
  }, [availableWei, handleApplyEvenSplit, token]);

  const handleResolveOverAllocation = useCallback(
    (walletId: number) => {
      if (!token) return;

      const otherSumWei = recipients
        .filter(recipient => recipient.walletId !== walletId)
        .reduce(
          (acc, recipient) => acc.add(amountToWei(recipient.amount, token.decimals)),
          BigNumber.from(0),
        );

      const remainingWei = availableWei.gt(otherSumWei)
        ? availableWei.sub(otherSumWei)
        : BigNumber.from(0);

      const adjustedAmount = weiToAmount(remainingWei, token.decimals);

      const nextRecipients = recipients.map(recipient =>
        recipient.walletId === walletId ? { ...recipient, amount: adjustedAmount } : recipient,
      );

      setRecipients(nextRecipients);
      setTotalAmount(weiToAmount(otherSumWei.add(remainingWei), token.decimals));
      setLastEditedWalletId(walletId);
    },
    [availableWei, recipients, setRecipients, setTotalAmount, token],
  );

  if (!token) {
    return null;
  }

  const tokenSymbol = token.symbol;
  const isNativeToken = token.type === 'native' || token.id === pair.token_fee.id;
  const rawBalanceFormatted = humanizeBn(rawAvailableWei, token.decimals);
  const availableFormatted = humanizeBn(availableWei, token.decimals);

  const sanitizeAmountInput = useCallback(
    (value: string) => {
      if (!token) return value;
      if (!value) return '';
      const normalized = value.replace(',', '.').replace(/[^0-9.]/g, '');
      const parts = normalized.split('.');

      const integerRaw = parts[0] ?? '';
      const fractionRaw = parts.slice(1).join('');

      const safeInteger =
        integerRaw.replace(/^0+(?=\d)/, '') || (normalized.startsWith('.') ? '0' : integerRaw);
      const clampedFraction = fractionRaw.slice(0, token.decimals);

      if (normalized.endsWith('.') && clampedFraction.length === fractionRaw.length) {
        return `${safeInteger || '0'}.`;
      }

      if (clampedFraction.length > 0) {
        return `${safeInteger || '0'}.${clampedFraction}`;
      }

      return safeInteger || '';
    },
    [token],
  );

  const totalExceedsAvailable = errors.total === 'Total exceeds available balance';

  return (
    <div className="flex flex-col gap-6">
      <SourceWalletSummary
        pair={pair}
        sourceWallet={sourceWallet}
        balance={
          <div className="flex flex-col gap-0.5">
            <span>
              Balance: {rawBalanceFormatted} {tokenSymbol}
            </span>
            {isNativeToken && estimatedGasFees.gasFeeWei.gt(0) && (
              <span className="text-xs text-gray-400">
                Available for transfer: {availableFormatted} {tokenSymbol}
              </span>
            )}
          </div>
        }
        footer={
          estimatedGasFees.gasFeeWei.gt(0) ? (
            <div className="flex flex-col gap-1">
              <span
                className={cn({
                  'text-amber-400': !hasInsufficientGasBalance,
                  'text-red-400': hasInsufficientGasBalance,
                })}
              >
                Estimated gas fee: {estimatedGasFees.gasFeeFormatted} {estimatedGasFees.gasSymbol} (
                {recipients.length} transfer{recipients.length > 1 ? 's' : ''})
              </span>
              {hasInsufficientGasBalance && (
                <span className="text-red-400 text-xs">
                  ⚠ Insufficient {estimatedGasFees.gasSymbol} balance (have:{' '}
                  {humanizeBn(nativeBalanceWei, gasPrice?.token.decimals ?? 18)}{' '}
                  {estimatedGasFees.gasSymbol})
                </span>
              )}
            </div>
          ) : undefined
        }
      />

      <InputField
        label={`Total amount (${tokenSymbol})`}
        type="decimal-number"
        value={totalAmount}
        setValue={value => handleTotalChange(sanitizeAmountInput(value))}
        placeholder="0.0"
        nodeRight={
          <button
            type="button"
            onClick={handleMax}
            className="px-3 py-1 text-sm text-purple-300 hover:text-purple-200 transition-colors"
          >
            Max
          </button>
        }
        errorMessage={errors.total}
      />

      <div className="flex flex-col gap-4">
        <div className="flex justify-between text-sm text-gray-400">
          <span>Selected wallets: {recipients.length}</span>
          <span>Token: {token.symbol}</span>
        </div>
        <div
          className="border border-[#2A2A2A] rounded-lg"
          style={{ maxHeight: 280, overflowY: 'auto' }}
        >
          <table className="w-full text-sm">
            <colgroup>
              <col />
              <col style={{ width: '140px' }} />
              <col style={{ width: '320px' }} />
            </colgroup>
            <thead>
              <tr className="bg-[#1E1E1E] text-left">
                <th className="px-3 py-2">Wallet</th>
                <th className="px-3 py-2 text-right">Balance ({tokenSymbol})</th>
                <th className="px-3 py-2 text-right">Amount ({tokenSymbol})</th>
              </tr>
            </thead>
            <tbody>
              {recipients.map(recipient => {
                const balanceFormatted = recipient.balanceFormatted;
                const errorMessage = errors.recipients[recipient.walletId];

                return (
                  <tr key={recipient.walletId} className="border-t border-[#2A2A2A]">
                    <td className="px-3 py-2 text-gray-200">
                      <TableAddressChip
                        address={recipient.walletAddress}
                        network={pair.network}
                        className="text-gray-200"
                      />
                    </td>
                    <td className="px-3 py-2 text-right text-gray-400">{balanceFormatted}</td>
                    <td className="px-3 py-2 align-top">
                      <input
                        value={recipient.amount}
                        onChange={event =>
                          handleRecipientAmountChange(
                            recipient.walletId,
                            sanitizeAmountInput(event.target.value),
                          )
                        }
                        placeholder="0.0"
                        className="w-full bg-[#141414] border border-[#2A2A2A] rounded px-3 py-2 text-right focus:outline-none"
                      />
                      {errorMessage && (
                        <div className="mt-1 flex items-center justify-between gap-2 text-xs text-red-400 whitespace-nowrap">
                          <span>{errorMessage}</span>
                          {totalExceedsAvailable && lastEditedWalletId === recipient.walletId && (
                            <button
                              type="button"
                              className="text-purple-300 hover:text-purple-200 transition-colors"
                              onClick={() => handleResolveOverAllocation(recipient.walletId)}
                            >
                              Max. available
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!recipients.length && (
                <tr>
                  <td colSpan={3} className="px-3 py-4 text-center text-gray-400">
                    No recipients selected
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
